import axios from '@/utils/axios';
import { AxiosResponse } from 'axios';
export * from './provider-city-prices';
import {
  SettingsFieldsData,
  SettingsFieldsResponseData,
  SettingsResponseData,
  CoverTypeObjectToPost,
  Category,
  CategoryObjectToPost,
  CoverType,
  Product,
  Provider,
  ProviderObjectToPost,
  CoverExtra,
  CoverExtraObjectToPost,
  SettingsResponseCategories,
  EmailTemplate,
  EmailTemplatePreviewPayload,
  EmailTemplatePreviewResponse,
  EmailTemplateStyle,
  EmailTemplateStylePayload,
  EmailTemplateUpdatePayload,
  ActivityObjectToPost,
  ActivityType,
  SettingsFromAPI,
} from '../types';

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const getResponseData = <T>(payload: unknown): T => {
  const record = toRecord(payload);

  if (record.data !== undefined) {
    const nestedRecord = toRecord(record.data);

    if (nestedRecord.data !== undefined) {
      return nestedRecord.data as T;
    }

    return record.data as T;
  }

  return payload as T;
};

const toCollection = (payload: unknown) => {
  if (Array.isArray(payload)) return payload;

  const record = toRecord(payload);
  const collection = ['email_templates', 'emailTemplates', 'templates', 'items']
    .map((key) => record[key])
    .find((value) => Array.isArray(value));

  return Array.isArray(collection) ? collection : [];
};

// API SETTINGS
// ----------------------------------------------------------------------

export async function getSettingsCategories(): Promise<
  SettingsResponseCategories[]
> {
  const {
    data: { data },
  }: SettingsResponseData = await axios.get('/settings/categories');
  return data;
}

export async function getSettingsFields(
  id: number
): Promise<SettingsFieldsData[]> {
  const {
    data: {
      data: { fields },
    },
  }: SettingsFieldsResponseData = await axios.get(
    `/settings/categories/${id}/fields`
  );

  return fields;
}

export async function saveSettingsFields(): Promise<any> {
  const {
    data: { data },
  }: any = await axios.put('/settings/fields/save');

  return data;
}

// ----------------------------------------------------------------------
// Activity types

// GET
export async function getActivityData(): Promise<ActivityType[]> {
  const {
    data: { data },
  } = await axios.get('/activities/types');
  return data;
}

export async function getSingleActivityData(id: number): Promise<ActivityType> {
  const {
    data: { data },
  } = await axios.get(`/activities/types/${id}`);
  return data;
}

// POST
export const postActivityData = async (postData: ActivityObjectToPost) =>
  axios.post('/activities/types', postData);

// PUT
export const updateActivityData = async (
  data: ActivityObjectToPost,
  ID: number
) => axios.put(`/activities/types/${ID}`, data);

// DELETE
export async function deleteActivityData(ID: number): Promise<AxiosResponse> {
  const data = await axios.delete(`/activities/types/${ID}`);

  return data;
}

// API EMAIL TEMPLATES
// ----------------------------------------------------------------------
export async function getEmailTemplates(): Promise<EmailTemplate[]> {
  const response = await axios.get('/emailTemplates');
  const payload = getResponseData<unknown>(response.data);

  return toCollection(payload) as EmailTemplate[];
}

export async function getEmailTemplate(id: number): Promise<EmailTemplate> {
  const response = await axios.get(`/emailTemplates/${id}`);
  return getResponseData<EmailTemplate>(response.data);
}

export async function putEmailTemplate(
  emailTemplate: EmailTemplateUpdatePayload,
  id: number
): Promise<EmailTemplate> {
  const response = await axios.put(`/emailTemplates/${id}`, emailTemplate);

  return getResponseData<EmailTemplate>(response.data);
}

export async function previewEmailTemplate(
  id: number,
  payload: EmailTemplatePreviewPayload
): Promise<EmailTemplatePreviewResponse> {
  const response = await axios.post(`/emailTemplates/${id}/preview`, payload);

  return getResponseData<EmailTemplatePreviewResponse>(response.data);
}

const normalizeEmailTemplateStyleResponse = (
  payload: unknown
): EmailTemplateStyle => {
  const data = getResponseData<unknown>(payload);
  const record = toRecord(data);

  return (record.style ??
    record.email_template_style ??
    record.emailTemplateStyle ??
    data) as EmailTemplateStyle;
};

export async function getEmailTemplateStyle(): Promise<EmailTemplateStyle> {
  const response = await axios.get('/emailTemplates/style');

  return normalizeEmailTemplateStyleResponse(response.data);
}

export async function putEmailTemplateStyle(
  payload: EmailTemplateStylePayload
): Promise<EmailTemplateStyle> {
  const response = await axios.put('/emailTemplates/style', payload);

  return normalizeEmailTemplateStyleResponse(response.data);
}

// ----------------------------------------------------------------------
// SETTINGS
export async function getSettingsData(): Promise<SettingsFromAPI> {
  const {
    data: {
      data: { settings },
    },
  } = await axios.get('/settings');
  return settings;
}

// ----------------------------------------------------------------------
// Categories

// GET
export async function getCategoryData(): Promise<Category[]> {
  const {
    data: { data },
  } = await axios.get('/products/categories');
  return data;
}

export async function getSingleCategoryData(
  categoryID: number
): Promise<Category> {
  const {
    data: { data },
  } = await axios.get(`/products/categories/${categoryID}`);
  return data;
}

// POST
export const postCategoryData = async (categoryData: CategoryObjectToPost) =>
  axios.post('/products/categories', categoryData);

// PUT
export const updateCategoryData = async (
  data: CategoryObjectToPost,
  categoryID: number
) => axios.put(`/products/categories/${categoryID}`, data);

// DELETE
export async function deleteCategory(
  categoryID: number
): Promise<AxiosResponse> {
  const data = await axios.delete(`/products/categories/${categoryID}`);

  return data;
}
// ----------------------------------------------------------------------
// Products

export async function getProductData(): Promise<Product[]> {
  const {
    data: { data },
  } = await axios.get('/products');
  return data;
}
export async function getSingleProductData(
  productID: number
): Promise<Product> {
  const {
    data: { data },
  } = await axios.get(`/products/${productID}`);
  return data;
}

export async function deleteProduct(productID: number): Promise<AxiosResponse> {
  const data = await axios.delete(`/products/${productID}`);

  return data;
}
// ----------------------------------------------------------------------
// Providers

export async function getProviderData(): Promise<Provider[]> {
  const {
    data: { data },
  } = await axios.get('/providers');
  return data;
}

export async function getSingleProviderData(ID: number): Promise<Provider> {
  const {
    data: { data },
  } = await axios.get(`/providers/${ID}`);
  return data;
}

export const postProviderData = async (postData: ProviderObjectToPost) =>
  axios.post('/providers', postData);

export const updateProviderData = async (
  data: ProviderObjectToPost,
  ID: number
) => axios.put(`/providers/${ID}`, data);

export async function deleteProviderData(ID: number): Promise<AxiosResponse> {
  const data = await axios.delete(`/providers/${ID}`);

  return data;
}
// ----------------------------------------------------------------------
// Cover level types

export async function getCoverLevelData(): Promise<CoverType[]> {
  const {
    data: { data },
  } = await axios.get('/products/cover-extra-types');
  return data;
}

export async function getSingleCoverLevelData(id: number): Promise<CoverType> {
  const {
    data: { data },
  } = await axios.get(`/products/cover-extra-types/${id}`);
  return data;
}

export const postCoverLevelData = async (postData: CoverTypeObjectToPost) =>
  axios.post('/products/cover-extra-types', postData);

export const updateCoverLevelData = async (
  data: CoverTypeObjectToPost,
  ID: number
) => axios.put(`/products/cover-extra-types/${ID}`, data);

export async function deleteCoverLevelData(ID: number): Promise<AxiosResponse> {
  const data = await axios.delete(`/products/cover-extra-types/${ID}`);

  return data;
}
// ----------------------------------------------------------------------
// Cover level extras

export async function getCoverExtrasData(): Promise<CoverExtra[]> {
  const {
    data: { data },
  } = await axios.get('/products/cover-extras');
  return data;
}
export async function getSingleCoverExtraData(id: number): Promise<CoverExtra> {
  const {
    data: { data },
  } = await axios.get(`/products/cover-extras/${id}`);
  return data;
}

export const postCoverExtraData = async (postData: CoverExtraObjectToPost) =>
  axios.post('/products/cover-extras', postData);

export const updateCoverExtraData = async (
  data: CoverExtraObjectToPost,
  ID: number
) => axios.put(`/products/cover-extras/${ID}`, data);

export async function deleteCoverExtraData(ID: number): Promise<AxiosResponse> {
  const data = await axios.delete(`/products/cover-extras/${ID}`);

  return data;
}
